import React, { useEffect, useMemo, useState } from 'react';

// Focused setup card opened from a count item's PAR control. It handles both a
// legacy item with no PAR and the durable SET PAR flag on a new item whose PAR
// was initialized to one unit. PAR belongs to the inventory item; Minimum
// Order belongs to one vendor product, so a multi-vendor item chooses the
// product whose real purchase_options.moq_packages value is being edited.
//
// Styling matches the count-completion status cards (CountWorkflowModal):
// same ink/soft-ink/line/surface palette, caution icon, uppercase eyebrow
// labels over strong values.
//
// The card deliberately does not preview an order. Ordering needs a specific
// package conversion, increment, fulfillment model, and vendor MOQ; pretending
// one item count unit equals one vendor package would show an answer that the
// Savings Review correctly computes differently.

const INK = '#3E2723';
const SOFT_INK = '#74645C';
const LINE = '#E8DDD0';
const SURFACE = '#FFFDF9';
const WARNING = '#F9A825';
const ACTION = '#C0603F';

const round6 = (n) => Math.round(n * 1e6) / 1e6;

const parseEntry = (value) => {
  const raw = String(value ?? '').trim();
  if (raw === '') return null;
  const n = Number.parseFloat(raw);
  return Number.isFinite(n) ? n : null;
};

/** "2.5" not "2.500000"; never exponent notation for the values counts use. */
export const formatQty = (n) => {
  if (n === null || n === undefined || !Number.isFinite(Number(n))) return '';
  return String(round6(Number(n)));
};

/**
 * Convert the focused card's displayed PAR back into the item's normalized
 * count unit. An automatically initialized PAR may display as a rounded
 * package fraction (1 each -> 0.08 case). Merely confirming that value must
 * preserve the exact stored one-each default instead of turning it into
 * 0.96 each; an actual edit still converts through the package normally.
 */
export function storedParFromFocusedSetup({
  enteredPar,
  unitsPerDisplayedUnit,
  reviewingAutoDefault = false,
  initialDisplayedPar = null,
  originalNormalizedPar = null,
} = {}) {
  const entered = Number(enteredPar);
  const initial = Number(initialDisplayedPar);
  const original = Number(originalNormalizedPar);
  if (
    reviewingAutoDefault
    && Number.isFinite(entered)
    && Number.isFinite(initial)
    && Number.isFinite(original)
    && Math.abs(entered - initial) < 1e-9
  ) {
    return round6(original);
  }
  const per = Number(unitsPerDisplayedUnit);
  return Number.isFinite(per) && per > 0
    ? round6(entered * per)
    : entered;
}

// Units that never take a plural "s": measures, abbreviations, and "each".
const NEVER_PLURAL = new Set([
  'each', 'ea', 'lb', 'lbs', 'oz', 'fl oz', 'kg', 'g', 'l', 'ml', 'gal', 'qt',
  'pt', 'ct', 'pc', 'pk', 'doz', 'dozen',
]);

export const unitWord = (unit, qty) => {
  const u = String(unit || 'each').trim() || 'each';
  if (Number(qty) === 1) return u;
  const lower = u.toLowerCase();
  if (NEVER_PLURAL.has(lower) || lower.endsWith('s')) return u;
  return `${u}s`;
};

export const activeMoqOptions = (options = []) => (Array.isArray(options) ? options : [])
  .filter((option) => option && option.status !== 'inactive');

export const moqOptionLabel = (option = {}) => {
  // vendor_description is the vendor's PRODUCT name, not the vendor. Keep
  // those two identities separate so a multi-option selector cannot label a
  // Brioche Buns row as though “Brioche Buns” were the supplier.
  const vendor = option.vendor_name
    || option.vendor?.display_name
    || option.vendor_name_text
    || 'Vendor product';
  const product = option.pack_description
    || option.vendor_description
    || (option.vendor_sku ? `SKU ${option.vendor_sku}` : '')
    || option.purchase_unit
    || '';
  return product && !String(vendor).toLowerCase().includes(String(product).toLowerCase())
    ? `${vendor} — ${product}`
    : String(vendor);
};

const initialOptionFor = (options, requestedId) => {
  if (!options.length) return null;
  if (requestedId != null) {
    const requested = options.find((option) => String(option.id) === String(requestedId));
    if (requested) return requested;
  }
  return options.find((option) => option.is_preferred === true) || options[0];
};

const eyebrowStyle = {
  fontSize: 11,
  fontWeight: 800,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: SOFT_INK,
};

const helpStyle = { fontSize: 11.5, color: SOFT_INK, lineHeight: 1.35, marginTop: 2 };

const bigValueStyle = { fontSize: '1.55rem', fontWeight: 900, color: INK, lineHeight: 1.1 };

const bigInputStyle = {
  width: 86,
  fontSize: '1.55rem',
  fontWeight: 900,
  color: INK,
  textAlign: 'center',
  padding: '4px 6px',
  borderRadius: 10,
  border: `2px solid ${LINE}`,
  backgroundColor: '#fff',
};

const CautionIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path d="M12 3 L22 20 H2 Z" fill={WARNING} stroke="#C77800" strokeWidth="1" strokeLinejoin="round" />
    <rect x="11.1" y="9" width="1.8" height="5.4" rx="0.9" fill={INK} />
    <circle cx="12" cy="16.7" r="1.15" fill={INK} />
  </svg>
);

export default function CountParSetupCard({
  itemName = '',
  countValue = 0,
  unitLabel = 'each',
  initialPar = null,
  purchaseOptions = [],
  initialPurchaseOptionId = null,
  reviewingAutoDefault = false,
  busy = false,
  // False when the card was opened before any count exists (the PAR badge on
  // an uncounted item) — "You counted 0" would be a claim nobody made.
  countEntered = true,
  onSave,
  onDismiss,
}) {
  const [parText, setParText] = useState(
    Number.isFinite(Number(initialPar)) && Number(initialPar) >= 0 ? formatQty(initialPar) : ''
  );
  const moqOptions = useMemo(() => activeMoqOptions(purchaseOptions), [purchaseOptions]);
  const initialOption = useMemo(
    () => initialOptionFor(moqOptions, initialPurchaseOptionId),
    [moqOptions, initialPurchaseOptionId],
  );
  const [selectedOptionId, setSelectedOptionId] = useState(
    initialOption?.id != null ? String(initialOption.id) : '',
  );
  const selectedOption = moqOptions.find(
    (option) => String(option.id) === String(selectedOptionId),
  ) || initialOption || null;
  const [moqText, setMoqText] = useState(
    Number.isFinite(Number(initialOption?.moq_packages)) && Number(initialOption.moq_packages) > 0
      ? formatQty(initialOption.moq_packages)
      : '1',
  );

  // The count loads purchase options asynchronously. If the modal opens while
  // that lookup is still settling, adopt the requested/preferred option when
  // it arrives instead of presenting a made-up MOQ 1 for an existing MOQ 3.
  useEffect(() => {
    const selectionStillExists = moqOptions.some(
      (option) => String(option.id) === String(selectedOptionId),
    );
    if (selectionStillExists || !initialOption) return;
    setSelectedOptionId(String(initialOption.id));
    setMoqText(
      Number.isFinite(Number(initialOption.moq_packages)) && Number(initialOption.moq_packages) > 0
        ? formatQty(initialOption.moq_packages)
        : '1',
    );
  }, [initialOption, moqOptions, selectedOptionId]);

  const parQty = parseEntry(parText);
  const canSave = !busy && parQty !== null && parQty >= 0;
  // A cleared vendor Minimum Order falls back to the schema default of one
  // purchase package — the card never saves a missing operational minimum.
  const effectiveMoq = (() => {
    const m = parseEntry(moqText);
    return m !== null && m > 0 ? m : 1;
  })();

  const numericGuard = (setter) => (e) => {
    if (/^\d*\.?\d*$/.test(e.target.value)) setter(e.target.value);
  };

  const handleSave = () => {
    if (!canSave || !onSave) return;
    onSave({
      par: parQty,
      purchaseOptionId: selectedOption?.id ?? null,
      moqPackages: selectedOption ? effectiveMoq : null,
    });
  };

  const handleOptionChange = (event) => {
    const nextId = event.target.value;
    const next = moqOptions.find((option) => String(option.id) === String(nextId));
    setSelectedOptionId(nextId);
    setMoqText(
      Number.isFinite(Number(next?.moq_packages)) && Number(next.moq_packages) > 0
        ? formatQty(next.moq_packages)
        : '1',
    );
  };

  const countText = formatQty(countValue);
  const countUnitText = unitWord(unitLabel, Number(countValue));

  const fieldRow = ({ testId, label, tooltip, help, control, emphasis = false, first = false }) => (
    <div
      key={label}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        padding: '12px 14px',
        borderTop: first ? 'none' : `1px solid ${LINE}`,
        backgroundColor: emphasis ? '#FFF8E1' : 'transparent',
      }}
      data-testid={testId}
    >
      <div style={{ minWidth: 0 }}>
        <div style={eyebrowStyle} title={tooltip}>{label}</div>
        {help ? <div style={helpStyle}>{help}</div> : null}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, flexShrink: 0 }}>
        {control}
      </div>
    </div>
  );

  return (
    <div data-testid="par-setup-card" style={{ width: '100%', textAlign: 'left', color: INK }}>
      {/* Header — caution icon + strong title, completion-card typography. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <CautionIcon />
        <h3 style={{ margin: 0, fontSize: '1.08rem', fontWeight: 800, color: INK }}>
          {reviewingAutoDefault ? 'Set PAR & minimum order' : 'Set a PAR'}
        </h3>
      </div>
      {itemName ? (
        <p style={{ margin: '0 0 8px', fontSize: 13, color: SOFT_INK }}>{itemName}</p>
      ) : null}
      <p style={{ margin: '0 0 12px', fontSize: 14, lineHeight: 1.45, color: INK }}>
        {reviewingAutoDefault ? (
          <>This new item started at <strong>PAR {formatQty(initialPar)} {unitWord(unitLabel, initialPar)}</strong>. Confirm or change its stocking target and vendor minimum.</>
        ) : countEntered ? (
          <>You counted <strong>{countText} {countUnitText}</strong>. </>
        ) : null}
        {!reviewingAutoDefault && <>Enter how much you want to keep on hand.</>}
      </p>

      {/* Count → PAR → one vendor product's Minimum Order. */}
      <div style={{ border: `1px solid ${LINE}`, borderRadius: 13, backgroundColor: SURFACE, overflow: 'hidden' }}>
        {fieldRow({
          testId: 'par-setup-count',
          label: 'Count',
          tooltip: 'What you have on hand right now.',
          help: countEntered ? 'What you just counted.' : 'Not counted yet.',
          first: true,
          control: (
            <>
              <span style={bigValueStyle}>{countText}</span>
              <span style={{ fontSize: 13, fontWeight: 700, color: SOFT_INK }}>{countUnitText}</span>
            </>
          ),
        })}
        {fieldRow({
          testId: 'par-setup-par',
          label: 'PAR',
          tooltip: 'PAR is your target inventory level. Your order is based on the difference between your Count and PAR.',
          help: 'How much you want to have on hand.',
          emphasis: true,
          control: (
            <>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                autoFocus
                data-testid="par-setup-par-input"
                aria-label="PAR"
                value={parText}
                onChange={numericGuard(setParText)}
                placeholder="0"
                style={{ ...bigInputStyle, borderColor: WARNING, backgroundColor: '#FFFDF4' }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: SOFT_INK }}>
                {unitWord(unitLabel, parQty ?? 2)}
              </span>
            </>
          ),
        })}
        {moqOptions.length > 1 ? fieldRow({
          testId: 'par-setup-vendor-product',
          label: 'Vendor product',
          tooltip: 'Minimum Order is stored separately for each way you buy this item.',
          help: 'Choose the package whose minimum you want to edit.',
          control: (
            <select
              data-testid="par-setup-option-select"
              aria-label="Vendor product"
              value={selectedOptionId}
              onChange={handleOptionChange}
              style={{ maxWidth: 190, border: `1px solid ${LINE}`, borderRadius: 8, padding: '7px 8px', color: INK, backgroundColor: '#fff', fontSize: 12 }}
            >
              {moqOptions.map((option) => (
                <option key={option.id} value={String(option.id)}>{moqOptionLabel(option)}</option>
              ))}
            </select>
          ),
        }) : null}
        {selectedOption ? fieldRow({
          testId: 'par-setup-moq',
          label: 'Minimum Order',
          tooltip: 'The smallest number of this vendor product that can be ordered.',
          help: moqOptions.length > 1
            ? 'Applies only to the selected vendor product.'
            : moqOptionLabel(selectedOption),
          control: (
            <>
              <input
                type="text"
                inputMode="decimal"
                autoComplete="off"
                data-testid="par-setup-moq-input"
                aria-label="Minimum Order"
                value={moqText}
                onChange={numericGuard(setMoqText)}
                placeholder="1"
                style={{ ...bigInputStyle, fontSize: '1.25rem', width: 74 }}
              />
              <span style={{ fontSize: 13, fontWeight: 700, color: SOFT_INK }}>
                {unitWord(selectedOption.purchase_unit || 'package', parseEntry(moqText) ?? 1)}
              </span>
            </>
          ),
        }) : (
          <div data-testid="par-setup-no-purchase-option" style={{ borderTop: `1px solid ${LINE}`, padding: '12px 14px', color: SOFT_INK, fontSize: 12.5, lineHeight: 1.45 }}>
            <strong style={{ color: INK }}>Minimum Order: 1 package by default.</strong><br />
            This item has no vendor product yet. Its first vendor product will start at a minimum of 1 package automatically.
          </div>
        )}
      </div>

      {selectedOption ? (
        <p data-testid="par-setup-moq-scope" style={{ margin: '9px 2px 0', fontSize: 11.5, color: SOFT_INK, lineHeight: 1.4 }}>
          MOQ is saved for {moqOptionLabel(selectedOption)} only. Other vendor products keep their own ordering rules.
        </p>
      ) : null}

      <div style={{ display: 'flex', gap: 10, marginTop: 14 }}>
        <button
          type="button"
          data-testid="par-setup-save"
          onClick={handleSave}
          disabled={!canSave}
          style={{
            flex: 1.4,
            padding: '12px 0',
            borderRadius: 10,
            border: `1px solid ${ACTION}`,
            backgroundColor: canSave ? ACTION : '#E4C7BA',
            color: '#fff',
            fontWeight: 800,
            fontSize: 14,
            cursor: canSave ? 'pointer' : 'not-allowed',
          }}
        >
          {busy ? 'Saving…' : 'Save & continue'}
        </button>
        <button
          type="button"
          data-testid="par-setup-dismiss"
          onClick={onDismiss}
          disabled={busy}
          style={{
            flex: 1,
            padding: '12px 0',
            borderRadius: 10,
            border: `1px solid ${LINE}`,
            backgroundColor: '#fff',
            color: SOFT_INK,
            fontWeight: 800,
            fontSize: 14,
            cursor: 'pointer',
          }}
        >
          Not now
        </button>
      </div>
    </div>
  );
}
